/**
 * Audio recording functionality for the chat input.
 */

import { i18n } from './i18n';
import { ChatAudioData } from './types';

export class AudioRecorder {
    private mediaRecorder: MediaRecorder | null = null;
    private audioChunks: Blob[] = [];
    private isRecording: boolean = false;
    private recordingStartTime: number = 0;
    private recordingTimerInterval: number | null = null;
    private pendingAudio: ChatAudioData | null = null;

    // DOM elements
    private recordBtn: HTMLButtonElement;
    private previewContainer: HTMLElement;
    private durationDisplay: HTMLElement;
    private removeBtn: HTMLButtonElement;

    constructor(
        recordBtn: HTMLButtonElement,
        previewContainer: HTMLElement,
        durationDisplay: HTMLElement,
        removeBtn: HTMLButtonElement
    ) {
        this.recordBtn = recordBtn;
        this.previewContainer = previewContainer;
        this.durationDisplay = durationDisplay;
        this.removeBtn = removeBtn;

        // Set up event listeners
        this.recordBtn.addEventListener('click', () => this.toggle());
        this.removeBtn.addEventListener('click', () => this.remove());
    }

    /**
     * Get the pending audio data, if any.
     */
    getAudio(): ChatAudioData | null {
        return this.pendingAudio;
    }

    /**
     * Clear the pending audio.
     */
    clear(): void {
        this.remove();
    }

    /**
     * Toggle audio recording on/off.
     */
    async toggle(): Promise<void> {
        if (this.isRecording) {
            this.stop();
        } else {
            await this.start();
        }
    }

    /**
     * Start audio recording using MediaRecorder API.
     */
    private async start(): Promise<void> {
        try {
            // Request microphone permission
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Determine best supported MIME type
            const mimeType = this.getSupportedMimeType();
            if (!mimeType) {
                throw new Error('No supported audio format');
            }

            this.mediaRecorder = new MediaRecorder(stream, { mimeType });
            this.audioChunks = [];

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };

            this.mediaRecorder.onstop = () => {
                // Stop all tracks to release the microphone
                stream.getTracks().forEach(track => track.stop());
                this.processRecordedAudio(mimeType);
            };

            this.mediaRecorder.onerror = () => {
                this.cancel();
                alert(i18n.recordingError);
            };

            // Start recording
            this.mediaRecorder.start();
            this.isRecording = true;
            this.recordingStartTime = Date.now();
            this.recordBtn.classList.add('recording');

            // Start timer display
            this.updateTimer();
            this.recordingTimerInterval = window.setInterval(() => {
                this.updateTimer();
            }, 1000);

        } catch (error) {
            console.error('Failed to start recording:', error);
            alert(i18n.microphoneAccessError);
        }
    }

    /**
     * Get the best supported audio MIME type for MediaRecorder.
     */
    private getSupportedMimeType(): string | null {
        const types = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4',
            'audio/wav'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        return null;
    }

    /**
     * Stop audio recording.
     */
    private stop(): void {
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
        }

        this.isRecording = false;
        this.recordBtn.classList.remove('recording');

        if (this.recordingTimerInterval !== null) {
            clearInterval(this.recordingTimerInterval);
            this.recordingTimerInterval = null;
        }
    }

    /**
     * Cancel recording and clean up without saving.
     */
    private cancel(): void {
        if (this.mediaRecorder) {
            if (this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.mediaRecorder = null;
        }

        this.isRecording = false;
        this.audioChunks = [];
        this.recordBtn.classList.remove('recording');

        if (this.recordingTimerInterval !== null) {
            clearInterval(this.recordingTimerInterval);
            this.recordingTimerInterval = null;
        }
    }

    /**
     * Update the recording timer display.
     */
    private updateTimer(): void {
        const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        this.durationDisplay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    /**
     * Process the recorded audio blob and prepare for sending.
     */
    private processRecordedAudio(mimeType: string): void {
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.audioChunks = [];

        // Calculate duration
        const durationSeconds = (Date.now() - this.recordingStartTime) / 1000;

        // Check audio size (max 10MB)
        const maxSize = 10 * 1024 * 1024;
        if (audioBlob.size > maxSize) {
            alert(i18n.recordingTooLong);
            return;
        }

        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64Data = (reader.result as string).split(',')[1];
            if (!base64Data) {
                alert(i18n.audioProcessError);
                return;
            }

            // Normalize MIME type (remove codecs part for API)
            const normalizedMimeType = mimeType.split(';')[0];

            this.pendingAudio = {
                data: base64Data,
                mime_type: normalizedMimeType,
                duration_seconds: durationSeconds
            };

            // Show preview
            this.showPreview(durationSeconds);
        };
        reader.onerror = () => {
            alert(i18n.audioReadError);
        };
        reader.readAsDataURL(audioBlob);
    }

    /**
     * Show the audio preview with duration.
     */
    private showPreview(durationSeconds: number): void {
        const minutes = Math.floor(durationSeconds / 60);
        const seconds = Math.floor(durationSeconds % 60);
        this.durationDisplay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        this.previewContainer.classList.remove('hidden');
        this.recordBtn.classList.add('has-audio');
    }

    /**
     * Remove the pending audio.
     */
    private remove(): void {
        this.pendingAudio = null;
        this.durationDisplay.textContent = '0:00';
        this.previewContainer.classList.add('hidden');
        this.recordBtn.classList.remove('has-audio');
    }

    /**
     * Format duration for display in messages.
     */
    static formatDuration(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
}
